import React from "react";
import { useTheme } from "@mui/material/styles";
import {
  AvatarContainer,
  CustomAvatar,
} from "./Avatar.styles";
import { SizeStyles, GradientStyles } from "./Avatar.variants";

type GradientStylesReturnType = ReturnType<typeof GradientStyles>;
type GradientStylesType = keyof GradientStylesReturnType;

export interface CustomVariantAvatarProps {
  size?: "sm" | "md" | "lg";
  gradientStyle?: GradientStylesType;
  initials?: string | null;
  fullName?: string;
  initialsBasedGradient?: boolean;
}

export const Avatar = ({
  size = "md",
  gradientStyle = "gradient2",
  initials = null,
  fullName,
  initialsBasedGradient = false,
  ...rest
}: CustomVariantAvatarProps) => {
  const theme = useTheme();

  const sizeStylesFn = SizeStyles(theme);
  const hash = fullName?.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) ?? 0;
  const gradientStylesFn = GradientStyles(theme, hash);

  // List of available gradients
  const gradientKeys = Object.keys(gradientStylesFn) as GradientStylesType[];

  // Function to get a consistent gradient style based on initials
  const getGradientStyle = (initials: string | null): GradientStylesType => {
    if (!initials) return "gradient2";
    const hash = initials
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return gradientKeys[hash % gradientKeys.length];
  };


  const computedGradientStyle = initialsBasedGradient
    ? fullName
      ? "gradient5"
      : getGradientStyle(initials)
    : gradientStyle;

  return (
    <AvatarContainer>
      <CustomAvatar
        sx={{
          ...sizeStylesFn[size],
          ...gradientStylesFn[computedGradientStyle],
        }}
        {...rest}
      >
        {initials}
      </CustomAvatar>
    </AvatarContainer>
  );
};

export interface AvatarProps extends React.ComponentProps<typeof Avatar> {
  as?: React.ElementType;
}

Avatar.displayName = "Avatar";